import {OrderIntakeOptionsDto} from '@hconnect/tuc-processes/orderIntake'
import {useMemo} from 'react'

import {OrderIntakeErrorType} from '../components/OrderIntakeErrorState'
import {OrderIntakeSite} from '../types/OrderIntakeSite.types'

export interface OrderIntakeValidationResult {
  isValid: boolean
  errorType: OrderIntakeErrorType | null
}

/**
 * Hook to validate order intake prerequisites.
 * Checks for required data and returns appropriate error types.
 *
 * @param jobSiteId The job site ID from URL params
 * @param jobSite The job site data from navigation state
 * @param options The normalized RMC options
 * @param isOptionsError Whether the options API call failed
 * @param isOptionsLoading Whether the options are currently being loaded
 * @returns Validation result with error type if invalid
 */
export const useOrderIntakeValidation = (
  jobSiteId: string | null,
  jobSite: OrderIntakeSite | undefined,
  options: OrderIntakeOptionsDto | undefined,
  isOptionsError: boolean,
  isOptionsLoading: boolean
): OrderIntakeValidationResult => {
  return useMemo(() => {
    // Check in priority order
    if (!jobSiteId) {
      return {isValid: false, errorType: 'noJobSiteId'}
    }

    if (!jobSite) {
      return {isValid: false, errorType: 'noJobSiteData'}
    }

    if (isOptionsError) {
      return {isValid: false, errorType: 'optionsLoadFailed'}
    }

    // Only validate options if we've attempted to load them
    // (after jobSite is available and no error occurred)
    if (jobSite && !isOptionsError && !isOptionsLoading) {
      if (!options) {
        return {isValid: false, errorType: 'noRequiredData'}
      }

      if (!options.contracts?.length) {
        return {isValid: false, errorType: 'noContracts'}
      }
    }

    return {isValid: true, errorType: null}
  }, [jobSiteId, jobSite, options, isOptionsError, isOptionsLoading])
}
